import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser, useOrganization } from "@clerk/clerk-react";
import { useApiRequest, useAuthenticatedQuery } from "@/hooks/use-api";
import { OrderProduct } from "@shared/schema";

/**
 * Submitted order as returned by GET /api/pc/orders: order fields the PC
 * dashboard displays plus the contractor org's contact info.
 */
export interface PcOrder {
  uid: string;
  // Contractor's PO reference (deprecated Onsemble order numbers are not surfaced)
  title: string;
  products: OrderProduct[] | null;
  notes: string | null;
  totalOrderCost: number;
  fulfillmentType: "delivery" | "pickup";
  submittedAt: string | null;
  createdByName: string | null;
  // PC pickup workflow, separate from the contractor order_status
  pickupStatus: PcOrderStatus;
  readyMarkedAt: string | null;
  contractor: {
    name: string;
    phone: string | null;
    hajocaCustomerId: string | null;
  };
}

export type PcOrderStatus = "new" | "ready_for_pickup";

export const getPcOrderStatus = (order: PcOrder): PcOrderStatus =>
  order.pickupStatus;

export const usePcOrders = () => {
  const { user } = useUser();
  const { organization } = useOrganization();

  const { data, isLoading, error } = useAuthenticatedQuery<PcOrder[]>(
    ["/api/pc/orders", organization?.id],
    {
      enabled: !!user && !!organization,
    },
  );

  return {
    orders: data || [],
    isLoading,
    error,
  };
};

export const useMarkOrderReady = () => {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const { apiRequest } = useApiRequest();

  return useMutation({
    mutationFn: async (orderId: string) => {
      const response = await apiRequest(
        "POST",
        `/api/pc/orders/${orderId}/ready`,
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          body?.error || `Failed to mark order ready: ${response.status}`,
        );
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/pc/orders", organization?.id],
      });
    },
  });
};
